import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
} from "react-native";
import { useSelector, useDispatch } from "react-redux";
import Svg, { Path, Rect } from "react-native-svg";
import TopOpportunitiesContainer from "../components/TopOpportunitiesContainer";
import StandupListContainer from "../components/StandupListContainer";
import NewsCard from "../components/NewsCard";
import StandupWelcome from "../components/StandupWelcome";
import StandupOverlay from "../components/StandupOverlay";
import NewsFeedOverlay from "../components/NewsFeedOverlay";import NewsDetailsOverlay from '../components/NewsDetailsOverlay';import TabSelector from "../components/TabSelector";
import DotPagination from "../components/DotPagination";
import NewsCardSkeleton from "../components/StandUpSkeletons/NewsCardSkeleton";
import StandUpRedirectScreen from "./StandUpRedirectScreen";
import StandUpCardsScreen from "./StandUpCardsScreen";
import TopOpportunitiesTable, {
  TopOpportunityItem,
} from "../components/TopOpportunitiesTable";
import TopOpportunityDetails from "../components/TopOpportunityDetails";
import { useStandUpDashboard, formatDateForTitle } from "../hooks/useStandUpDashBoard";
import { useAddNews } from "../hooks/useAddNews";
import { useStandUpScreenHandlers } from "../hooks/useStandUpScreenHandlers";
import { formatUserName } from "../../../utils/emailTouserName";
import { RootState } from "../../../store";
import { resetStandupRedirect, setSessionData, fetchLighthouseTasks } from "../../../store/slices/standupRedirectSlice";
import {
  createApiClient,
} from "../../../api/apiClient";
import { getSessionLockService } from "../../../api/sessionLockService";
import { checkIsSubmittedBtnClick, clearNews, clearSuccessMessage, clearToastNotification, setLastFetchedLocation, setToastNotification } from "../../../store/slices/standUpSlice";
import { clearToastNotification as clearActionPlanToastNotification } from '../../../store/slices/actionPlanSlice';
import StandUpEmptyState from "./StandUpEmptyStateScreen";
import { useToast } from '../../../components/LhToastContainer';
import SessionLockProvider from '../../../modules/sessionLock/components/SessionLockProvider';
import StandUpErrorModal from '../components/StandUpErrorModal';
import { useSelectedLocation } from "../../../hooks/useSelectedLocation";
import StandUpTopOpportunitiesScreen from './StandUpTopOpportunitiesScreen';
import TopOpportunitiesBoardPrint, { TopOpportunitiesBoardPrintRef } from "../components/Print/TopOpportunitiesBoardPrint";
import TopOpportunitiesTablePrint, {TopOpportunitiesTablePrintRef} from "../components/Print/TopOpportunitiesTablePrint";
import logger from "../../../utils/logger";
import { useActionPlanDashboard } from "../../ActionPlan/hooks/useActionPlanDashboard";
import ChartsContainer from '../components/charts/ChartsContainer';

interface StandUpScreenProps {
  onBackPress?: () => void;
  onHomePress?: () => void;
  initialRoute?: string;
  actionPlanId?: string;
}

const StandUpScreen: React.FC<StandUpScreenProps> = ({
  onBackPress,
  onHomePress,
  initialRoute,
  actionPlanId,
}) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const lockReleasedRef = React.useRef(false); // Track if lock was already released to prevent double-release
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [newsFeedOverlayVisible, setNewsFeedOverlayVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<"board" | "table">("board");
  const [currentNewsPage, setCurrentNewsPage] = useState(0);
  const [showRedirectScreen, setShowRedirectScreen] = useState(false);
  const [showCardsScreen, setShowCardsScreen] = useState(false);
  const [standupType, setStandupType] = useState<string>("");
  const [standupDate, setStandupDate] = useState<string>("");
  const [standupSessionId, setStandupSessionId] = useState<string>("");
  const [standupId, setStandupId] = useState<string>("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [editStandupData, setEditStandupData] = useState<any>(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [viewStandupData, setViewStandupData] = useState<any>(null);
  const [currentLockToken, setCurrentLockToken] = useState<string>("");
  const [currentLockResourceId, setCurrentLockResourceId] = useState<string>("");
  const [favoriteOpportunities, setFavoriteOpportunities] = useState<string[]>(
    [],
  );
  const topOpportunitiesBoardRef = useRef<TopOpportunitiesBoardPrintRef | null>(null);
  const topOpportunitiesTableRef = useRef<TopOpportunitiesTablePrintRef | null>(null);
  const [togglingNewsId, setTogglingNewsId] = useState<number | null>(null);
  const selectedLocation  = useSelectedLocation();
  const [lastSelectedLocation, setLastSelectedLocation] = useState(""); // Track last selected location for change detection
  const [newsDetailsOverlayVisible, setNewsDetailsOverlayVisible] = useState(false);
  const [selectedNewsId, setSelectedNewsId] = useState<string>('');
  const [showTopOpportunitiesScreen, setShowTopOpportunitiesScreen] = useState(false);
  const {
    standupListItems,
    inProgressItems,
    completedItems,
    overdueItems,
    newsList: newsListFromApi,
    fetchStandUpData,
    fetchNewsData,
    isLoadingStandup,
    isLoadingTopOpportunities,
    isLoadingNews,
    isTogglingFavorite,
    showActionPlanDetails,
    selectedActionPlanId,
    selectedActionPlanData,
    handleBackFromDetails,
    handleViewActionPlanDetails,
    handleViewActionPlan,
    handleOpenActionPlanDetailsById,
    setTopOpportunities,
    setInProgressItems,
    setOverdueItems,
    setCompletedItems,
    fetchCIDashboard,
    pagination,
    currentPage,
    handlePageChange,
    handlePageSizeChange,
    handleToggleFavoriteOpportunity,
    fetchTopOpportunitiesData,
  } = useStandUpDashboard();

  const handledInitialNavRef = useRef(false);
  useEffect(() => {
    if (handledInitialNavRef.current) return;
    if (initialRoute !== 'TopOpportunityDetails') return;
    if (!actionPlanId) return;

    handledInitialNavRef.current = true;
    handleOpenActionPlanDetailsById(String(actionPlanId));
  }, [initialRoute, actionPlanId, handleOpenActionPlanDetailsById]);

  // Track when user is on main dashboard vs sub-screens
  const isMainDashboard = !showRedirectScreen && !showCardsScreen && !showActionPlanDetails;
  const prevIsMainDashboard = React.useRef(isMainDashboard);

  const userRoleId = useSelector(
    (state: RootState) =>
      state.appData?.userInfo?.roleId ?? state.auth?.user?.roleId ?? null,
  );

  const periodWeekData = useSelector((state: RootState) => state.appData.periodWeek?.data || []);

   // Only fetch data when location changes or data is not available
    useEffect(() => {
      if (selectedLocation?.id != null && userRoleId != null) {
        const locationChanged = lastSelectedLocation !== selectedLocation?.displayText;     
        if (locationChanged) {
          setLastSelectedLocation(selectedLocation?.displayText);
          logger.log("Fetching standup data - location changed or no data", lastSelectedLocation);
          // Clear news immediately when location changes for instant reset
          dispatch(clearNews());
          setNewsList([]);
          fetchStandUpData();
          fetchTopOpportunitiesData();
          fetchNewsData();
          fetchCIDashboard();
          // Fetch lighthouse tasks data
          if (periodWeekData?.period && periodWeekData?.year) {
            dispatch(fetchLighthouseTasks({
              locationId: selectedLocation.id,
              locationName: selectedLocation.displayText,
              period: periodWeekData.period,
              year: periodWeekData.year,
            }) as any);
          }
        } else {
          logger.log("Using cached standup data from Redux");
        }
      }
    }, [lastSelectedLocation, selectedLocation?.displayText, userRoleId]);
  // Get top opportunities from Redux (already fetched by useStandUpDashboard)
  const topOpportunitiesTableData = useSelector(
    (state: RootState) => state.standUp.topOpportunities,
  );

  // Get user email and location from Redux
  const userEmail = useSelector(
    (state: RootState) =>
      state.appData?.userInfo?.email ?? state.auth?.user?.email ?? "",
  );
  const toastNotification = useSelector((state: RootState) => state.standUp?.toastNotification);

  // Initialize the useAddNews hook for centralized news API operations
  const { toggleNewsFavorite } = useAddNews({ selectedLocation });

  // Clear ActionPlan toast notification on mount to prevent cross-module notifications
  useEffect(() => {
    dispatch(clearActionPlanToastNotification());
  }, [dispatch]);

  // Display toast notification when it appears in Redux
  useEffect(() => {
    if (toastNotification) {
      toast.showToast({
        header: toastNotification.header,
        subtext: toastNotification.subtext,
        variant: toastNotification.variant,
        position: toastNotification.position || 'top',
        duration: toastNotification.duration || 4000,
      });

      // Clear the toast from Redux after displaying
      dispatch(clearToastNotification());
    }
  }, [toastNotification, toast, dispatch]);

  const [newsList, setNewsList] = useState<
    Array<{
      id: string;
      title: string;
      description: string;
      date: string;
      createdBy: string;
      is_favourite?: boolean;
      is_truncated?: boolean;
    }>
  >([]);

  // Initialize all screen handlers using custom hook
  const {
    generateStandupSessionId,
    handleOpenOverlay,
    handleCloseOverlay,
    handleContinue,
    handleBackToStandUp,
    handleClearStandup,
    handleEditStandup,
    handleViewStandup,
    handleViewMoreCards,
    handleBackFromCards,
    handleOpenNewsFeedOverlay,
    handleCloseNewsFeedOverlay,
    handleSaveNews,
  } = useStandUpScreenHandlers({
    setOverlayVisible,
    setShowRedirectScreen,
    setShowCardsScreen,
    setStandupType,
    setStandupDate,
    setStandupSessionId,
    setNewsFeedOverlayVisible,
    setNewsList,
    isEditMode,
    standupId,
    currentLockToken,
    setCurrentLockToken,
    currentLockResourceId,
    setCurrentLockResourceId,
    lockReleasedRef,
    setIsEditMode,
    setEditStandupData,
    setIsViewMode,
    setViewStandupData,
    setStandupId,
    fetchStandUpData,
    fetchTopOpportunitiesData,
    fetchNewsData,
  });

  // Sync with API news data - always sync, including empty arrays to reset on location change
  useEffect(() => {
    setNewsList(newsListFromApi || []);
  }, [newsListFromApi]);

  useEffect(() => {
    // Ensure data is fetched by the hook if not already available
    if (!standupListItems || standupListItems.length === 0) {
      console.log("No standup data available, relying on useStandUpDashboard hook.");
    }
  }, [standupListItems]);

  /**
   * Fetch top opportunities on component mount (when navigating to Stand Up dashboard)
   */
  useEffect(() => {
    if (selectedLocation?.id != null) {
      console.log('[StandUp] Fetching top opportunities on dashboard mount/navigation');
      fetchTopOpportunitiesData(currentPage, pagination?.pageSize || 10);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  /**
   * Refetch top opportunities when returning to main dashboard from sub-screens
   */
  useEffect(() => {
    // Refetch when transitioning back to main dashboard from a sub-screen
    if (isMainDashboard && !prevIsMainDashboard.current && selectedLocation?.id != null) {
      console.log('[StandUp] Refetching top opportunities on return to dashboard');
      fetchTopOpportunitiesData(currentPage, pagination?.pageSize || 10);
    }
    prevIsMainDashboard.current = isMainDashboard;
  }, [isMainDashboard, selectedLocation?.id, currentPage, pagination?.pageSize, fetchTopOpportunitiesData]);

  /**
   * Cleanup effect: Release lock when navigating away
   */
  useEffect(() => {
    return () => {
      // This cleanup runs when component unmounts (e.g., navigating to another screen)
      // Guard: Don't release if already released by handler
      if (lockReleasedRef.current) {
        console.log('[StandUp] 🔐 Lock already released by handler, skipping cleanup release');
        return;
      }
      
      if (isEditMode && standupId && currentLockToken) {
        console.log('[StandUp] 🔐 Cleaning up: Releasing lock on component unmount');
        const lockService = getSessionLockService();
        if (lockService) {
          lockService.releaseLock(standupId, currentLockToken).catch((error) => {
            console.error('[StandUp] Error releasing lock on unmount:', error);
          });
        }
      }
    };
  }, [isEditMode, standupId, currentLockToken]);

    // Use ActionPlan hook for status change functionality
  const { handleStatusChange } = useActionPlanDashboard(selectedLocation, { 
    isStandup: true, 
    skipCalendarAndFilters: true ,
    topOpportunitiesData: topOpportunitiesTableData, // Pass current top opportunities data for immediate UI update
  });

  // Handler for news favorite toggling (not provided by useStandUpScreenHandlers)
  // Note: On the dashboard, we only show global news and the star icon is hidden
  // This handler is still needed for the NewsFeedOverlay which may show favorite toggles
  const handleToggleForNewsFavorite = async (newsId: number) => {
    try {
      // Set loading state for this specific news item
      setTogglingNewsId(newsId);

      // Find the news item in the current list
      const newsItem = newsList.find(item => parseInt(item.id) === newsId);
      
      if (!newsItem) {
        console.error("News item not found:", newsId);
        dispatch(
          setToastNotification({
            header: "Error",
            subtext: "News item not found",
            variant: "error",
            position: "top",
            duration: 3000,
          })
        );
        setTogglingNewsId(null);
        return;
      }

      // Use the hook to toggle favorite status
      const result = await toggleNewsFavorite(newsId, newsItem as any);

      if (result.success) {
        // For global news (dashboard context), wasDisassociated should never be true
        // But handle it just in case - update local state with the new favorite status
        const newFavoriteStatus = !newsItem.is_favourite;
        setNewsList((prev) =>
          prev.map((item) =>
            parseInt(item.id) === newsId
              ? { ...item, is_favourite: newFavoriteStatus }
              : item
          )
        );

        console.log("Successfully toggled favorite status for news ID:", newsId);
      }
    } catch (error: any) {
      console.error("Error toggling favorite for news:", error);
    } finally {
      // Clear loading state after API call completes (success or error)
      setTogglingNewsId(null);
    }
  };

  const noStandups = !isLoadingStandup && (!standupListItems || standupListItems.length === 0);

  // If we're showing the redirect screen, render it instead (wrapped in SessionLockProvider for modal access)
  if (showRedirectScreen) {
    return (
      <SessionLockProvider onLockTimeout={handleBackToStandUp}>
        <StandUpRedirectScreen
          onBackPress={handleBackToStandUp}
          onHomePress={onHomePress}
          standupType={standupType}
          standupDate={standupDate}
          standupSessionId={standupSessionId}
          standupId={standupId}
          isEditMode={isEditMode}
          editStandupData={editStandupData}
          isViewMode={isViewMode}
          viewStandupData={viewStandupData}
        />
      </SessionLockProvider>
    );
  }

  // If we're showing the cards screen, render it instead (wrapped in SessionLockProvider for modal access)
  if (showCardsScreen) {
    return (
      <SessionLockProvider onLockTimeout={handleBackFromCards}>
        <StandUpCardsScreen
          onBackPress={handleBackFromCards}
          items={standupListItems.map((item) => ({
            ...item,
            id: item.id.toString(),
            onEditPress: () => handleEditStandup(item),
            onViewPress: () => handleViewStandup(item),
          }))}
        />
      </SessionLockProvider>
    );
  }

  // If Top Opportunities full screen requested, render it as a new screen (full screen)
  if (showTopOpportunitiesScreen) {
    return (
      <SessionLockProvider onLockTimeout={() => setShowTopOpportunitiesScreen(false)}>
        <StandUpTopOpportunitiesScreen
          onBackPress={() => setShowTopOpportunitiesScreen(false)}
          inProgressItems={inProgressItems}
          completedItems={completedItems}
          overdueItems={overdueItems}
          isLoadingTopOpportunities={isLoadingTopOpportunities}
          onViewActionPlan={handleViewActionPlan}
        />
      </SessionLockProvider>
    );
  }

  // Render TopOpportunitiesDetails if navigating to it (wrapped in SessionLockProvider for modal access)
  if (showActionPlanDetails) {
    const handleActionPlanSaved = (updatedData: any) => {
      if (updatedData?.STATUS === "deleted" && updatedData?.ID) {
        setTopOpportunities((prevData: TopOpportunityItem[]) =>
          prevData.filter((item) => item.ID !== Number(updatedData.ID)),
        );
        // Remove from categorized lists
        setInProgressItems((prev) => prev.filter((item) => item.ID !== Number(updatedData.ID)));
        setOverdueItems((prev) => prev.filter((item) => item.ID !== Number(updatedData.ID)));
        setCompletedItems((prev) => prev.filter((item) => item.ID !== Number(updatedData.ID)));
        //  setSelectedActionPlanData(null);
        return;
      }

      // Update the action plan in the list
      setTopOpportunities((prevData: TopOpportunityItem[]) => {
        return prevData.map((item: TopOpportunityItem) =>
          item.ID === updatedData.ID ? updatedData : item,
        );
      });

      // Update categorized lists: remove from all first
      setInProgressItems((prev) => prev.filter((item) => item.ID !== updatedData.ID));
      setOverdueItems((prev) => prev.filter((item) => item.ID !== updatedData.ID));
      setCompletedItems((prev) => prev.filter((item) => item.ID !== updatedData.ID));

      // Then add to the correct list based on STATUS
      if (updatedData.STATUS === "in_progress") {
        setInProgressItems((prev) => [...prev, updatedData]);
      } else if (updatedData.STATUS === "completed") {
        setCompletedItems((prev) => [...prev, updatedData]);
      } else if (updatedData.STATUS === "overdue") {
        setOverdueItems((prev) => [...prev, updatedData]);
      }
    };
    return (
      <SessionLockProvider onLockTimeout={handleBackFromDetails}>
        <TopOpportunityDetails
          actionPlanId={selectedActionPlanId || undefined}
          actionPlanData={selectedActionPlanData}
          onBackPress={handleBackFromDetails}
          backButtonText="Back to Top Opportunities Dashboard"
          showTopOpportunityToggle={true}
          onActionPlanSaved={handleActionPlanSaved}
          onActionPlanDeleted={(deletedId: string) => {
            // Remove deleted item immediately from the list
            setTopOpportunities(prev => prev.filter(item => String(item.ID) !== String(deletedId)));
            // Remove from categorized lists
            setInProgressItems((prev) => prev.filter((item) => String(item.ID) !== String(deletedId)));
            setOverdueItems((prev) => prev.filter((item) => String(item.ID) !== String(deletedId)));
            setCompletedItems((prev) => prev.filter((item) => String(item.ID) !== String(deletedId)));
          }}
        />
      </SessionLockProvider>
    );
  }

  // Paginate news - 4 per page
  const newsPerPage = 4;
  const totalNewsPages = Math.ceil(newsList.length / newsPerPage);
  const paginatedNews = newsList.slice(
    currentNewsPage * newsPerPage,
    (currentNewsPage + 1) * newsPerPage,
  );

  const AddIcon = () => (
    <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <Path
        d="M15.8334 10.8337H10.8334V15.8337H9.16675V10.8337H4.16675V9.16699H9.16675V4.16699H10.8334V9.16699H15.8334V10.8337Z"
        fill="#004895"
      />
    </Svg>
  );

  const PrintIcon = () => (
    <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <Path
        d="M12.6666 5.33333H11.9999V2H3.99992V5.33333H3.33325C2.22659 5.33333 1.33325 6.22667 1.33325 7.33333V11.3333H3.99992V14H11.9999V11.3333H14.6666V7.33333C14.6666 6.22667 13.7733 5.33333 12.6666 5.33333ZM5.33325 3.33333H10.6666V5.33333H5.33325V3.33333ZM10.6666 12.6667H5.33325V10H10.6666V12.6667ZM11.9999 10V8.66667H3.99992V10H2.66659V7.33333C2.66659 6.96667 2.96659 6.66667 3.33325 6.66667H12.6666C13.0333 6.66667 13.3333 6.96667 13.3333 7.33333V10H11.9999Z"
        fill="#034895"
      />
      <Path
        d="M11.9999 8.33333C12.3681 8.33333 12.6666 8.03486 12.6666 7.66667C12.6666 7.29848 12.3681 7 11.9999 7C11.6317 7 11.3333 7.29848 11.3333 7.66667C11.3333 8.03486 11.6317 8.33333 11.9999 8.33333Z"
        fill="#034895"
      />
    </Svg>
  );

  const DiagonalArrowIcon = () => (
    <Svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <Path d="M4 0.5H28C29.933 0.5 31.5 2.067 31.5 4V28C31.5 29.933 29.933 31.5 28 31.5H4C2.067 31.5 0.5 29.933 0.5 28V4C0.5 2.067 2.067 0.5 4 0.5Z" fill="#FFFFFF" />
      <Path d="M4 0.5H28C29.933 0.5 31.5 2.067 31.5 4V28C31.5 29.933 29.933 31.5 28 31.5H4C2.067 31.5 0.5 29.933 0.5 28V4C0.5 2.067 2.067 0.5 4 0.5Z" stroke="#D8E7FB" />
      <Path d="M22 15.3333V10H16.6667L18.86 12.1933L12.1933 18.86L10 16.6667V22H15.3333L13.14 19.8067L19.8067 13.14L22 15.3333Z" fill="#034895" />
    </Svg>
  );

  return (
    <SessionLockProvider>
      <ScrollView style={styles.container}>
        <View style={styles.content}>
          <StandupWelcome
            openFilterOverlay={handleOpenOverlay}
            clearStandup={handleClearStandup}
            hideCreateButton={noStandups} // Pass the condition to hide the button
          />

          <View style={styles.standupListSection}>
            {noStandups ? (
              <View style={styles.emptyStateWrapper}>
                <StandUpEmptyState onCreatePress={handleOpenOverlay} />
              </View>
            ) : (
              <StandupListContainer
                items={standupListItems.map((item) => ({
                  ...item,
                  id: item.id.toString(),
                  onEditPress: () => {
                    console.log('✏️ [StandupListItem] onEditPress callback triggered');
                    handleEditStandup(item);
                  },
                  onViewPress: () => handleViewStandup(item),
                }))}
                maxVisibleItems={4}
                onViewMorePress={handleViewMoreCards}
                isLoading={isLoadingStandup}
              />
            )}
          </View>

          {/* Top Opportunities Section - Hide when no standups */}
          {!noStandups && (
            <View style={styles.topOpportunitiesSection}>
              {/* Section Header with Print Button */}
              <View style={styles.sectionHeaderRow}>
                <View style={styles.sectionHeaderLeft}>
                  <Text style={styles.sectionHeader} numberOfLines={1} ellipsizeMode="tail">
                    Top Opportunities
                  </Text>
                </View>
                <View style={styles.sectionHeaderRight}>
                  <TouchableOpacity
                    style={styles.sectionHeaderIconButton}
                    onPress={() => setShowTopOpportunitiesScreen(true)}
                    accessibilityLabel="Top opportunities action"
                  >
                    <DiagonalArrowIcon />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.printButton}
                    onPress={() => {
                      if (activeTab === "board") {
                        topOpportunitiesBoardRef.current?.print();
                      } else {
                        topOpportunitiesTableRef.current?.print();
                      }
                    }}
                  >
                    <PrintIcon />
                    <Text style={styles.printText}>Print</Text>
                  </TouchableOpacity>
                </View>
              </View>

              {/* Centered Tabs */}
              <View style={styles.tabsContainer}>
                <TabSelector activeTab={activeTab} onTabChange={setActiveTab} />
              </View>

              {/* Three Containers in a Row - Board Tab Only */}
              {activeTab === "board" ? (
                <View style={styles.containersRow}>
                  <TopOpportunitiesContainer
                    title="In progress"
                    count={inProgressItems?.length}
                    items={inProgressItems}
                    width="32.5%"
                    height={380}
                    onAddPress={handleViewActionPlan}
                    isLoading={isLoadingTopOpportunities}
                  />
                  <TopOpportunitiesContainer
                    title="Done"
                    count={completedItems?.length}
                    items={completedItems}
                    width="32.5%"
                    height={380}
                    onAddPress={handleViewActionPlan}
                    isLoading={isLoadingTopOpportunities}
                  />
                  <TopOpportunitiesContainer
                    title="Overdue"
                    count={overdueItems?.length}
                    items={overdueItems}
                    width="32.5%"
                    height={380}
                    onAddPress={handleViewActionPlan}
                    isLoading={isLoadingTopOpportunities}
                  />
                </View>
              ) : (
                <View style={styles.tableView}>
                  <TopOpportunitiesTable
                    data={topOpportunitiesTableData}
                    isLoading={isLoadingTopOpportunities || isTogglingFavorite}
                    onFavoriteToggle={handleToggleFavoriteOpportunity}
                    onViewDetailsPress={handleViewActionPlanDetails}
                    maxRecordsToShow={5}
                    currentPage={currentPage}
                    onStatusChange={handleStatusChange}
                    totalPages={pagination?.totalPages || 1}
                    pageSize={pagination?.pageSize || 10}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </View>
            
              )}

              {/* News Section */}
              <View style={styles.newsHeader}>
                <Text style={styles.newsTitle} numberOfLines={1} ellipsizeMode="tail">
                  News
                </Text>
                <TouchableOpacity
                  style={styles.addNewButton}
                  onPress={handleOpenNewsFeedOverlay}
                >
                  <AddIcon />
                  <Text style={styles.addNewText}>Add new</Text>
                </TouchableOpacity>
              </View>

              {/* News Cards in a Row - Dashboard view: hide star icons */}
              <View style={styles.newsCardsRow}>
                {isLoadingNews ? (
                  // Show skeleton loading for news cards
                  [...Array(4)].map((_, index) => (
                    <NewsCardSkeleton key={`news-skeleton-${index}`} width="23.5%" />
                  ))
                ) : (
                  paginatedNews.map((news) => {
                    console.log("Rendering news card for news item:", news);
                    const newsIdNum = parseInt(news.id) || 0;
                    
                    return (
                      <NewsCard
                        key={news.id}
                        news_id={newsIdNum}
                        title={news.title}
                        date={news.date}
                        is_favourite={news.is_favourite ?? false}
                        handleToggleFavorite={() => {
                          // No-op for dashboard - starring disabled
                        }}
                        description={news.description}
                        width="24%"
                        isLoading={false}
                        disabled={true}
                        is_truncated={news.is_truncated}
                        onViewMore={() => {
                          setSelectedNewsId(news.id);
                          setNewsDetailsOverlayVisible(true);
                        }}
                        hideStarIcon={true} // Hide star icon on dashboard - starring is standup-specific
                      />
                    )
                  })
                )}
              </View>

              {/* Dot Pagination */}
              {!isLoadingNews && totalNewsPages > 1 && (
                <DotPagination
                  totalDots={totalNewsPages}
                  activeDot={currentNewsPage}
                  onDotPress={setCurrentNewsPage}
                />
              )}
            </View>
            
          )}

          {/* Top Opportunities Stats — 6 responsive Victory donut charts - Hide when no standups */}
          {!noStandups && (
            <ChartsContainer
              data={topOpportunitiesTableData}
              isLoading={isLoadingTopOpportunities}
            />
          )}

          <StandupOverlay
            visible={overlayVisible}
            onDismiss={handleCloseOverlay}
            onContinue={handleContinue}
            standupListItems={standupListItems}
            userRoleId={userRoleId}
          />

          <NewsFeedOverlay
            visible={newsFeedOverlayVisible}
            onDismiss={handleCloseNewsFeedOverlay}
            onSave={handleSaveNews}
            userEmail={userEmail}
            locationId={selectedLocation?.id ?? null}
            locationName={selectedLocation?.displayText ?? ""}
            showDeleteButton={true}
          />
          <NewsDetailsOverlay
            visible={newsDetailsOverlayVisible}
            onDismiss={() => setNewsDetailsOverlayVisible(false)}
            newsId={selectedNewsId}
          />
          {/* Centralized Error Modal */}
          <StandUpErrorModal />
        </View>
      </ScrollView>

      {/* Render Print Components as regular components - hidden off-screen */}
      <View style={{ position: "absolute", left: -9999, top: -9999 }}>
        {activeTab === "board" ? (
          <TopOpportunitiesBoardPrint
            ref={topOpportunitiesBoardRef}
            inProgressItems={inProgressItems}
            completedItems={completedItems}
            overdueItems={overdueItems}
          />
        ) : (
          <TopOpportunitiesTablePrint
            ref={topOpportunitiesTableRef}
            data={topOpportunitiesTableData}
            isLoading={isLoadingTopOpportunities || isTogglingFavorite}
            onFavoriteToggle={handleToggleFavoriteOpportunity}
            onViewDetailsPress={handleViewActionPlanDetails}
            maxRecordsToShow={5}
            currentPage={currentPage}
            totalPages={pagination?.totalPages || 1}
            pageSize={pagination?.pageSize || 10}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </View>
    </SessionLockProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F5F9FF",
  },
  content: {
    padding: 24,
  },
  standupListSection: {
    flex: 1,
    width: '100%',
    // marginTop: 8,
  },
  topOpportunitiesSection: {
    marginTop: 20,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 24,
  },
  sectionHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  sectionHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flexShrink: 1,
    minWidth: 0,
  },
  sectionHeaderRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    justifyContent: "flex-end",
    marginLeft: "auto",
  },
  sectionHeader: {
    padding: 8,
    // Figma-like heading H5 bold
    fontFamily: 'Inter',
    fontSize: 24,
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: 32,
    color: '#2D2D2D',
    // prevent wrapping / layout shift and enable ellipsis
    overflow: 'hidden',
    flexShrink: 1,
    minWidth: 0,
  },
  sectionHeaderIconButton: {
    flexShrink: 0,
  },
  printButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderColor: "#D8E7FB",
    borderWidth: 1,
    borderRadius: 4,
  },
  printText: {
    fontSize: 14,
    fontWeight: "400",
    color: "#034895",
  },
  tabsContainer: {
    alignItems: "center",
    marginBottom: 24,
  },
  header: {
    fontSize: 28,
    fontWeight: "600",
    color: "#1C1C1C",
    marginBottom: 24,
  },
  containersRow: {
    flexDirection: "row",
    gap: 12,
    justifyContent: "space-between",
    marginBottom: 24,
  },
  tableView: {
    marginBottom: 24,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    overflow: "hidden",
  },
  newsHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  newsTitle: {
    // Figma H5 bold styling
    fontFamily: 'Inter',
    fontSize: 24,
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: 32,
    color: '#2D2D2D',
    overflow: 'hidden',
  },
  addNewButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: "#FFFFFF",
    borderRadius: 9999,
    borderWidth: 1,
    borderColor: "#004895",
  },
  addNewText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#004895",
  },
  newsCardsRow: {
    flexDirection: "row",
    gap: 12,
    justifyContent: "flex-start",
    flexWrap: "nowrap",
    marginBottom: 4,
  },
  emptyStateWrapper: {
    flex: 1,
    width: '100%',
    justifyContent: "center",
    alignItems: "stretch",
    // padding: 20,
  },
});

export default StandUpScreen;
